import React, { useEffect, useState } from "react";
import { Check, Pencil, Trash2, X } from "lucide-react";
import FieldEditor from "./FieldEditor";

function FieldRow({ field, editing, onEdit, onDelete, onCancelEdit, onSaveEdit }) {
  if (editing) {
    return (
      <div className="mb-3">
        <FieldEditor field={field} onCancel={onCancelEdit} onSave={onSaveEdit} />
      </div>
    );
  }

  const displayLabel =
    field.type === "h2" || field.type === "body"
      ? field.content || field.label
      : field.label;

  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1 text-left">
        {field.type === "h2" && (
          <p className="text-base font-semibold text-[#1D2739]">{displayLabel}</p>
        )}
        {field.type === "body" && (
          <p className="text-sm text-gray-600">{displayLabel}</p>
        )}
        {field.type !== "h2" && field.type !== "body" && (
          <>
            <label className="mb-1 block text-sm font-medium text-[#1D2739]">
              {field.label}
              {field.required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            {field.type === "text" && (
              <div className="rounded-lg bg-gray-100 px-3 py-2.5 text-sm text-gray-400">
                {field.placeholder || " "}
              </div>
            )}
            {field.type === "dropdown" && (
              <div className="rounded-lg bg-gray-100 px-3 py-2.5 text-sm text-gray-400">
                Select an option
              </div>
            )}
            {field.type === "checkbox" && (
              <div className="space-y-1">
                {(field.options || []).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" disabled />
                    {opt}
                  </label>
                ))}
              </div>
            )}
            {(field.type === "file" || field.type === "logo") && (
              <div className="rounded-lg bg-gray-100 px-3 py-2.5 text-sm text-gray-500">
                Choose file
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1 pt-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function FormSectionCard({
  section,
  selected,
  onSelect,
  onRename,
  onEditField,
  onDeleteField,
  editingFieldId,
  setEditingFieldId,
}) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(section.title);

  useEffect(() => {
    setTitle(section.title);
  }, [section.title]);

  const commitRename = (e) => {
    e?.stopPropagation();
    onRename(title);
    setRenaming(false);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect();
      }}
      className={`mb-4 cursor-pointer rounded-xl border bg-white px-4 py-4 text-left transition ${
        selected ? "border-blue-500 shadow-sm" : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        {renaming ? (
          <div className="flex flex-1 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename(e);
                if (e.key === "Escape") {
                  setTitle(section.title);
                  setRenaming(false);
                }
              }}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none"
            />
            <button type="button" onClick={commitRename} className="text-green-600">
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setTitle(section.title);
                setRenaming(false);
              }}
              className="text-gray-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <span className="text-sm font-semibold text-[#1D2739]">{section.title}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
                setRenaming(true);
              }}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {selected && (
        <div onClick={(e) => e.stopPropagation()}>
          {section.fields.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">
              Choose customization elements to add in section
            </p>
          ) : (
            section.fields.map((field) => (
              <FieldRow
                key={field.id}
                field={field}
                editing={editingFieldId === field.id}
                onEdit={() => setEditingFieldId(field.id)}
                onDelete={() => onDeleteField(field.id)}
                onCancelEdit={() => setEditingFieldId(null)}
                onSaveEdit={(changes) => {
                  onEditField(field.id, changes);
                  setEditingFieldId(null);
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
